import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { usePermissions } from '../hooks/usePermissions';
import { addNotification } from '../store/slices/notificationSlice';
import { fetchDataSource } from '../store/slices/dataSourceSlice';
import { isDataSourceConfigured } from '../utils/dataSourceHelpers';

interface ContentMasterGuardProps {
  children: React.ReactNode;
}

/**
 * Blocks access to Content Master pages until the user has a JTL-Wawi data source configured.
 *
 * The check is purely store-driven - `state.dataSource.data` is pre-populated by
 * `initializeUserData` during auth, so we don't fire an additional request here.
 * When the data source is missing, we:
 *   1. Raise an error notification explaining *why* the user was bounced.
 *   2. Redirect (`replace`) to `/profile?direction=dataSource` so the database tab opens.
 *
 * This runs BEFORE any Content Master page is rendered, which prevents the page's
 * own fetches (workspace-config, product list, …) from hitting the Wawi endpoints
 * and returning `content_master_wawi_connection_failed`.
 */
const ContentMasterGuard: React.FC<ContentMasterGuardProps> = ({ children }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const dataSource = useAppSelector((state) => state.dataSource.data);
  const dataSourceLoading = useAppSelector((state) => state.dataSource.loading);
  const isDataInitialized = useAppSelector((state) => state.auth.isDataInitialized);
  const { can } = usePermissions();
  const canRead = can('content_master_read');

  const configured = useMemo(() => isDataSourceConfigured(dataSource), [dataSource]);

  // Only decide once the initial data-source fetch has completed; otherwise we might
  // redirect on the initial null render, before `initializeUserData` finishes.
  const readyToDecide = isDataInitialized && !dataSourceLoading;

  // The store can be stale: Wawi Bridge pairing happens outside the web app
  // (inside the exe), so a DataSource may have appeared since the bootstrap
  // fetch. Before bouncing the user, force ONE fresh fetch and re-decide -
  // while it is in flight `dataSourceLoading` keeps `readyToDecide` false.
  const [rechecked, setRechecked] = useState(false);
  useEffect(() => {
    if (readyToDecide && canRead && !configured && !rechecked) {
      setRechecked(true);
      dispatch(fetchDataSource({ force: true }));
    }
  }, [readyToDecide, canRead, configured, rechecked, dispatch]);

  const shouldRedirect = readyToDecide && canRead && !configured && rechecked;

  // Fire the notification exactly once per redirect - StrictMode in dev mounts effects twice.
  const notifiedRef = useRef(false);
  useEffect(() => {
    if (!shouldRedirect || notifiedRef.current) {
      return;
    }
    notifiedRef.current = true;
    dispatch(
      addNotification({
        type: 'error',
        title: t('error'),
        message: t('content_master_requires_data_source'),
      })
    );
  }, [dispatch, shouldRedirect, t]);

  if (!readyToDecide) {
    // Wait for initial data-source fetch rather than redirect-flicker.
    return null;
  }

  // Subuser without content_master_read: the plugin is off-limits entirely
  // (checked before the data-source redirect - a profile redirect would only
  // confuse a user who cannot use the plugin anyway).
  if (!canRead) {
    return (
      <div className="dashboard-empty-state">
        <p className="dashboard-empty-state-text">{t('subuser_no_permission_title')}</p>
        <p className="dashboard-empty-state-hint">{t('subuser_no_permission_description')}</p>
      </div>
    );
  }

  if (!configured) {
    if (!rechecked) {
      // Recheck effect has not dispatched yet (first render after bootstrap).
      return null;
    }
    return <Navigate to="/profile?direction=dataSource" replace />;
  }

  return <>{children}</>;
};

export default ContentMasterGuard;
